"use client";
import { createTooltip } from "@gluestack-ui/core/tooltip/creator";
import type { VariantProps } from "@gluestack-ui/utils/nativewind-utils";
import { tva, withStyleContext } from "@gluestack-ui/utils/nativewind-utils";
import {
  AnimatePresence,
  Motion,
  MotionComponentProps,
} from "@legendapp/motion";
import { cssInterop } from "nativewind";
import React from "react";
import { Text, View, ViewStyle } from "react-native";

type IMotionViewProps = React.ComponentProps<typeof View> &
  MotionComponentProps<typeof View, ViewStyle, unknown, unknown, unknown>;

const MotionView = Motion.View as React.ComponentType<IMotionViewProps>;

export const UITooltip = createTooltip({
  Root: withStyleContext(View),
  Content: MotionView,
  Text: Text,
  AnimatePresence: AnimatePresence,
});

cssInterop(MotionView, { className: "style" });

const tooltipStyle = tva({
  base: "w-full h-full web:pointer-events-none",
});

const tooltipContentStyle = tva({
  base: "py-1 px-3 rounded-md bg-background-900 web:pointer-events-auto",
  variants: {
    arrow: {
      bottom:
        "before:content-[''] before:absolute before:bottom-[-6px] before:left-1/2 before:transform before:-translate-x-1/2 before:w-0 before:h-0 before:border-l-[6px] before:border-l-transparent before:border-r-[6px] before:border-r-transparent before:border-t-[6px] before:border-t-background-900",
      top: "before:content-[''] before:absolute before:top-[-6px] before:left-1/2 before:transform before:-translate-x-1/2 before:w-0 before:h-0 before:border-l-[6px] before:border-l-transparent before:border-r-[6px] before:border-r-transparent before:border-b-[6px] before:border-b-background-900",
      "top right":
        "before:content-[''] before:absolute before:top-[-6px] before:right-[18px] before:w-0 before:h-0 before:border-l-[6px] before:border-l-transparent before:border-r-[6px] before:border-r-transparent before:border-b-[6px] before:border-b-background-900",
      "top left":
        "before:content-[''] before:absolute before:top-[-6px] before:left-[18px] before:w-0 before:h-0 before:border-l-[6px] before:border-l-transparent before:border-r-[6px] before:border-r-transparent before:border-b-[6px] before:border-b-background-900",
      "bottom right":
        "before:content-[''] before:absolute before:bottom-[-6px] before:right-[18px] before:w-0 before:h-0 before:border-l-[6px] before:border-l-transparent before:border-r-[6px] before:border-r-transparent before:border-t-[6px] before:border-t-background-900",
      "bottom left":
        "before:content-[''] before:absolute before:bottom-[-6px] before:left-[18px] before:w-0 before:h-0 before:border-l-[6px] before:border-l-transparent before:border-r-[6px] before:border-r-transparent before:border-t-[6px] before:border-t-background-900",
      left: "before:content-[''] before:absolute before:left-[-6px] before:top-1/2 before:transform before:-translate-y-1/2 before:w-0 before:h-0 before:border-t-[6px] before:border-t-transparent before:border-b-[6px] before:border-b-transparent before:border-r-[6px] before:border-r-background-900",
      "left top":
        "before:content-[''] before:absolute before:left-[-6px] before:top-[18px] before:w-0 before:h-0 before:border-t-[6px] before:border-t-transparent before:border-b-[6px] before:border-b-transparent before:border-r-[6px] before:border-r-background-900",
      "left bottom":
        "before:content-[''] before:absolute before:left-[-6px] before:bottom-[18px] before:w-0 before:h-0 before:border-t-[6px] before:border-t-transparent before:border-b-[6px] before:border-b-transparent before:border-r-[6px] before:border-r-background-900",
      right:
        "before:content-[''] before:absolute before:right-[-6px] before:top-1/2 before:transform before:-translate-y-1/2 before:w-0 before:h-0 before:border-t-[6px] before:border-t-transparent before:border-b-[6px] before:border-b-transparent before:border-l-[6px] before:border-l-background-900",
      "right top":
        "before:content-[''] before:absolute before:right-[-6px] before:top-[18px] before:w-0 before:h-0 before:border-t-[6px] before:border-t-transparent before:border-b-[6px] before:border-b-transparent before:border-l-[6px] before:border-l-background-900",
      "right bottom":
        "before:content-[''] before:absolute before:right-[-6px] before:bottom-[18px] before:w-0 before:h-0 before:border-t-[6px] before:border-t-transparent before:border-b-[6px] before:border-b-transparent before:border-l-[6px] before:border-l-background-900",
    },
  },
});

const tooltipTextStyle = tva({
  base: "font-normal tracking-normal web:select-none text-xs text-typography-50",

  variants: {
    isTruncated: {
      true: "line-clamp-1 truncate",
    },
    bold: {
      true: "font-bold",
    },
    underline: {
      true: "underline",
    },
    strikeThrough: {
      true: "line-through",
    },
    size: {
      "2xs": "text-2xs",
      xs: "text-xs",
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
      xl: "text-xl",
      "2xl": "text-2xl",
      "3xl": "text-3xl",
      "4xl": "text-4xl",
      "5xl": "text-5xl",
      "6xl": "text-6xl",
    },
    sub: {
      true: "text-xs",
    },
    italic: {
      true: "italic",
    },
    highlight: {
      true: "bg-yellow-500",
    },
  },
});

type ITooltipProps = React.ComponentProps<typeof UITooltip> &
  VariantProps<typeof tooltipStyle> & { className?: string };
type ITooltipContentProps = React.ComponentProps<typeof UITooltip.Content> &
  VariantProps<typeof tooltipContentStyle> & { className?: string };
type ITooltipTextProps = React.ComponentProps<typeof UITooltip.Text> &
  VariantProps<typeof tooltipTextStyle> & { className?: string };

const Tooltip = React.forwardRef<
  React.ComponentRef<typeof UITooltip>,
  ITooltipProps
>(function Tooltip({ className, ...props }, ref) {
  return (
    <UITooltip
      ref={ref}
      className={tooltipStyle({ class: className })}
      {...props}
    />
  );
});

const TooltipContent = React.forwardRef<
  React.ComponentRef<typeof UITooltip.Content>,
  ITooltipContentProps & { className?: string }
>(function TooltipContent({ className, arrow, ...props }, ref) {
  return (
    <UITooltip.Content
      ref={ref}
      {...props}
      className={tooltipContentStyle({
        class: className,
        arrow,
      })}
      pointerEvents="auto"
    />
  );
});

const TooltipText = React.forwardRef<
  React.ComponentRef<typeof UITooltip.Text>,
  ITooltipTextProps & { className?: string }
>(function TooltipText({ size, className, ...props }, ref) {
  return (
    <UITooltip.Text
      ref={ref}
      className={tooltipTextStyle({ size, class: className })}
      {...props}
    />
  );
});

Tooltip.displayName = "Tooltip";
TooltipContent.displayName = "TooltipContent";
TooltipText.displayName = "TooltipText";

export { Tooltip, TooltipContent, TooltipText };
